import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { RequestEditAddressBody } from '../../../../../shared/api/hscan/address';
import { useUserState } from '../../../../../shared/hooks/accountHook';
import { useUserAddress } from '../../../../../shared/hooks/addressHook';
import editIcon from '../../../assets/icons/icon_edit.svg';
import peopleIcon from '../../../assets/icons/icon_people.svg';
import phoneIcon from '../../../assets/icons/icon_phone.svg';
import { ColoredIcon, IconButton } from '../Icon';

import { AddressTextField } from './AddressTextField';
import { SlotGrid, SlotGridWrapper } from './SlotWrapper';
import { TextFieldSlot } from './TextFieldSlot';
import { SlotRef } from './types';

export interface AddressForm {
  address: RequestEditAddressBody;
  name: string;
  phone: string;
}
export interface InvalidState {
  address: boolean;
  name: boolean;
  phone: boolean;
}
interface AddresseeSlotsProps {
  invalidState: InvalidState;
  onChangeForm: React.Dispatch<React.SetStateAction<AddressForm>>;
  form: AddressForm;
}
const AddressSlotGrid = styled(SlotGrid)`
  row-gap: 24px;
`;
export const AddresseeSlots = ({
  invalidState,
  onChangeForm,
  form,
}: AddresseeSlotsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.request.payment',
  });
  const [user] = useUserState();
  const { data: userAddress } = useUserAddress();
  const setAddress = useCallback(
    (address: RequestEditAddressBody) =>
      onChangeForm(prev => ({ ...prev, address })),
    [onChangeForm],
  );
  const setName = useCallback(
    (name: string) => onChangeForm(prev => ({ ...prev, name })),
    [onChangeForm],
  );
  const setPhone = useCallback(
    (phone: string) => onChangeForm(prev => ({ ...prev, phone })),
    [onChangeForm],
  );
  useEffect(() => {
    if (userAddress) {
      setAddress(userAddress);
    }
    setName(user?.name ?? '');
    setPhone(user?.telecom ?? '');
  }, [setAddress, setName, setPhone, user?.name, user?.telecom, userAddress]);

  const nameSlotRef = useRef<SlotRef>(null);
  const phoneSlotRef = useRef<SlotRef>(null);
  const [nameReadOnly, setNameReadOnly] = useState(true);
  const [phoneReadOnly, setPhoneReadOnly] = useState(true);

  return (
    <SlotGridWrapper>
      <AddressSlotGrid $marginTop="37px" $width={450} $columnGap={56}>
        <AddressTextField
          value={form.address}
          onChange={setAddress}
          label={`${t('receiver.address')}:`}
          invalid={invalidState.address}
        />
        <TextFieldSlot
          ref={nameSlotRef}
          label={`${t('receiver.name')}:`}
          textAlign="center"
          value={form.name}
          onChange={setName}
          readOnly={nameReadOnly}
          onBlur={() => setNameReadOnly(true)}
          icon={
            <ColoredIcon
              width="24px"
              height="24px"
              imageUrl={peopleIcon}
              imageWidth="24px"
              color={'var(--HScan_Light_Blue, #2e5be0)'}
            />
          }
          invalid={invalidState.name}
          fullwidth>
          <IconButton
            onClick={() => {
              setNameReadOnly(false);
              setTimeout(() => nameSlotRef.current?.inputs[0]?.focus(), 0);
            }}
            imageUrl={editIcon}
            width="24px"
            margin="0px 4px 0px 17px"
            height="24px"
            imageWidth="14px"
            color={'var(--HPACS_Text_Light_Grey, #a3a6ab)'}
          />
        </TextFieldSlot>
        <TextFieldSlot
          ref={phoneSlotRef}
          label={`${t('receiver.phone')}:`}
          textAlign="center"
          value={form.phone}
          onChange={setPhone}
          readOnly={phoneReadOnly}
          onBlur={() => setPhoneReadOnly(true)}
          icon={
            <ColoredIcon
              width="24px"
              height="24px"
              imageUrl={phoneIcon}
              imageWidth="24px"
              color={'var(--HScan_Light_Blue, #2e5be0)'}
            />
          }
          invalid={invalidState.phone}
          fullwidth>
          <IconButton
            onClick={() => {
              setPhoneReadOnly(false);
              setTimeout(() => phoneSlotRef.current?.inputs[0]?.focus(), 0);
            }}
            imageUrl={editIcon}
            width="24px"
            margin="0px 4px 0px 17px"
            height="24px"
            imageWidth="14px"
            color={'var(--HPACS_Text_Light_Grey, #a3a6ab)'}
          />
        </TextFieldSlot>
      </AddressSlotGrid>
    </SlotGridWrapper>
  );
};
